import type { CarteComitePRIsME } from './comitePRIsMECommunicationData';
import { participantsComitePRIsME } from './comitePRIsMECommunicationData';

export const carteEbaucheCadreBZ = {
  title: 'Ébauche de cadre client BZ',
  category: 'Cadre référentiel',
  description: 'Ébauche structurée du Cadre BZ unifié : 6 piliers principaux alignés ITIL, COBIT, NIST et PRIsME, plus 2 dimensions transversales pour garder la cohérence d’exécution.',
  aiSuggestion: 'Proposition de cadre unifié BZ pour validation au Comité PRIsME',
  kpiScore: 94,
  imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&auto=format&fit=crop&q=80',
  inKnowledgeBase: false,
};

export const communicationEbaucheCadreBZ: CarteComitePRIsME['communication'] = {
  id: 'comm-prisme-ebauche-cadre-bz-001',
  titre: 'Ébauche de cadre client BZ',
  date: '2026-03-31',
  heure: '20:45',
  dureeMinutes: 28,
  lieu: 'Comité PRIsME',
  objectifPrincipal: 'Proposer un cadre unifié BZ tenant compte des référentiels ITIL, COBIT, NIST et de la logique PRIsME, avec 6 piliers principaux et 2 dimensions transversales pour guider la gouvernance, l’opération et la transformation.',
  statut: 'Planifiée',
  participants: participantsComitePRIsME,
  ordreDuJour: [
    {
      id: 'ebauche-1',
      numero: 1,
      titre: 'Pourquoi un cadre BZ unifié',
      description: 'Poser la logique de convergence entre les grands référentiels et la réalité opérationnelle PRIsME.',
      presentateur: participantsComitePRIsME[1],
      dureeMinutes: 5,
      niveau: 'Stratégique',
      statut: 'Planifié',
      points: [
        {
          id: 'ebauche-1-1',
          titre: 'Intention du cadre',
          contenu: `Le Cadre BZ proposé ne vise pas à recopier un référentiel unique. Il vise à créer une structure simple, vendable et pilotable qui respecte la gouvernance de COBIT, la logique de service d’ITIL, la profondeur de sécurité de NIST et la réalité terrain de PRIsME.

• **COBIT** apporte surtout la gouvernance, l’alignement, les responsabilités et le contrôle.
• **ITIL 4** apporte surtout la gestion des services, la création de valeur et l’amélioration continue.
• **NIST** apporte surtout la cybersécurité, le risque, la protection et la résilience.
• **PRIsME** apporte la structure BZ : comités, livrables, personnalisation client, projets, qualité et reproductibilité.

L’objectif n’est donc pas de créer un cadre théorique de plus. L’objectif est d’avoir une grille commune qui aide à penser, présenter, diagnostiquer, vendre et livrer.` ,
          dureeMinutes: 5,
          graphiques: [],
          concepts: ['Convergence', 'Référentiels', 'Cadre unifié']
        }
      ]
    },
    {
      id: 'ebauche-2',
      numero: 2,
      titre: 'Les 6 piliers principaux',
      description: 'Définir les six dimensions structurantes du Cadre BZ unifié.',
      presentateur: participantsComitePRIsME[0],
      dureeMinutes: 15,
      niveau: 'Stratégique',
      statut: 'Planifié',
      points: [
        {
          id: 'ebauche-2-1',
          titre: '1. Gouvernance, stratégie & organisation',
          contenu: `Ce pilier couvre la vision TI, l’alignement business, les rôles et responsabilités, les comités, la priorisation, les décisions, les politiques et le modèle de gouvernance.

**Ça couvre notamment :**
• Vision TI et alignement d’affaires
• Gouvernance, rôles, RACI et comités
• Priorisation, arbitrages et décisions
• Politiques, règles de gestion et cadre organisationnel
• Pilotage stratégique, tactique et opérationnel

**Référentiels dominants :** COBIT + ITIL + PRIsME`,
          dureeMinutes: 2,
          graphiques: [],
          concepts: ['Gouvernance', 'Stratégie', 'Organisation']
        },
        {
          id: 'ebauche-2-2',
          titre: '2. Architecture, infrastructure & cloud',
          contenu: `Ce pilier couvre l’architecture TI, les infrastructures, les plateformes, les réseaux, l’hébergement, le cloud, la capacité, la résilience et la continuité technique.

**Ça couvre notamment :**
• Architecture technologique
• Serveurs, réseaux, postes et plateformes
• Hébergement, infonuagique et redondance
• Capacité, performance et continuité technique
• Standards d’infrastructure et dépendances techniques

**Référentiels dominants :** ITIL + COBIT + pratiques BZ Infonuagique`,
          dureeMinutes: 2,
          graphiques: [],
          concepts: ['Architecture', 'Infrastructure', 'Cloud']
        },
        {
          id: 'ebauche-2-3',
          titre: '3. Applications, données & intégrations',
          contenu: `Ce pilier couvre les systèmes métiers, les applications, les flux de données, les intégrations, l’ownership applicatif et la qualité de l’information.

**Ça couvre notamment :**
• Systèmes métiers et portefeuille applicatif
• Données, qualité, cycle de vie et classification
• Intégrations, interopérabilité et flux applicatifs
• Responsabilités applicatives et dépendances fonctionnelles
• Lecture du patrimoine applicatif et informationnel

**Référentiels dominants :** COBIT + ITIL + NIST (information) + PRIsME`,
          dureeMinutes: 2,
          graphiques: [],
          concepts: ['Applications', 'Données', 'Intégrations']
        },
        {
          id: 'ebauche-2-4',
          titre: '4. Services, opérations & expérience utilisateur',
          contenu: `Ce pilier couvre la prestation quotidienne : support TI, incidents, demandes, changements, performance, disponibilité, continuité de service et expérience utilisateur.

**Ça couvre notamment :**
• Catalogue de services et prestation TI
• Incidents, demandes, changements et problèmes
• Disponibilité, performance et continuité de service
• Indicateurs de service et qualité perçue
• Expérience utilisateur interne et client

**Référentiels dominants :** ITIL 4 + PRIsME`,
          dureeMinutes: 2,
          graphiques: [],
          concepts: ['Services', 'Opérations', 'Expérience utilisateur']
        },
        {
          id: 'ebauche-2-5',
          titre: '5. Cybersécurité, risque & conformité',
          contenu: `Ce pilier couvre la sécurité de l’information, les contrôles, les risques, la conformité, les accès, la protection, la détection, la réponse et la reprise.

**Ça couvre notamment :**
• Gouvernance cyber et posture de sécurité
• Gestion des risques et acceptation du risque
• Contrôles, conformité et exigences réglementaires
• Protection, détection, réponse et reprise
• Identités, accès et protection des données

**Référentiels dominants :** NIST CSF 2.0 + COBIT + ISO 27001`,
          dureeMinutes: 2,
          graphiques: [],
          concepts: ['Cybersécurité', 'Risque', 'Conformité']
        },
        {
          id: 'ebauche-2-6',
          titre: '6. Transformation, innovation & IA',
          contenu: `Ce pilier couvre l’évolution des capacités, l’amélioration de la valeur, l’automatisation, l’IA, les expérimentations structurées et la transformation numérique.

**Ça couvre notamment :**
• Transformation numérique et évolution du modèle TI
• Innovation structurée et expérimentation utile
• IA, automatisation et gains de productivité
• Veille, modernisation et différenciation
• Passage de l’idée à la capacité durable

**Référentiels dominants :** PRIsME + ITIL (amélioration) + NIST AI RMF`,
          dureeMinutes: 3,
          graphiques: [],
          concepts: ['Transformation', 'Innovation', 'IA']
        }
      ]
    },
    {
      id: 'ebauche-3',
      numero: 3,
      titre: 'Les 2 dimensions transversales',
      description: 'Préciser ce qui doit traverser tous les piliers plutôt que de devenir un silo de plus.',
      presentateur: participantsComitePRIsME[2],
      dureeMinutes: 8,
      niveau: 'Tactique',
      statut: 'Planifié',
      points: [
        {
          id: 'ebauche-3-1',
          titre: 'A. Gestion des actifs & parties prenantes',
          contenu: `Cette dimension est transversale parce qu’elle touche tous les piliers. Les actifs, les propriétaires, les parties prenantes, les dépendances et les responsabilités ne vivent pas dans un seul silo.

**Ça couvre notamment :**
• Inventaire et classification des actifs
• Parties prenantes, ownership et responsabilités
• Dépendances entre actifs, services, applications et équipes
• Vision de patrimoine TI et traçabilité
• Lien entre SERVO, PRIsME et la gouvernance opérationnelle

**Rôle dans le cadre :** fournir la structure commune qui relie l’infrastructure, les applications, les services, la sécurité et la gouvernance.`,
          dureeMinutes: 4,
          graphiques: [],
          concepts: ['Actifs', 'Parties prenantes', 'Patrimoine TI']
        },
        {
          id: 'ebauche-3-2',
          titre: 'B. Gestion de projet & amélioration continue',
          contenu: `Cette dimension est transversale parce qu’elle représente le moteur d’évolution de tous les piliers. Les projets et l’amélioration continue ne doivent pas être des silos; ils doivent être la mécanique de progression du cadre.

**Ça couvre notamment :**
• Gestion de projet, jalons, tâches, RACI et priorités
• Portefeuille, suivi, gouvernance et exécution
• Post-mortem, leçons apprises et qualité
• Maturité, mesures, progression et optimisation
• Capacité à transformer chaque pilier de manière répétable

**Rôle dans le cadre :** permettre la transformation contrôlée, documentée et mesurable de l’ensemble du Cadre BZ.`,
          dureeMinutes: 4,
          graphiques: [],
          concepts: ['Projet', 'Amélioration continue', 'Transformation']
        }
      ]
    }
  ]
};
